<template>
  <fts-dropdown-select
    placement="bottom"
    strategy-fixed
    content-class="sort-content sort-dropdown-menu"
    :stop-propagation-class="referenceClass"
    class="fts-sort"
    @hide="onClose">
    <!-- toggle -->
    <fts-btn slot="toggle" text color="primary" text-transform-init>
      <span v-if="sort && sort.field">
        Sorted: {{ sort.field.label || sort.field.displayName }}
        {{ sort.direction === 0 ? "Ascending" : "Descending" }}
      </span>
      <span v-else>Sort</span>
    </fts-btn>
    <!-- menu -->
    <template #menu>
      <fts-dropdown-select
        class="sort-info"
        placement="bottom-start"
        append-to-body
        prevent-overflow-alt-axis
        prevent-overflow-tether-off
        :offset="[0, 6]"
        :selected-option="sort"
        :append-content-class="`sort-info-content sort-info-dropdown-menu ${referenceClass}`"
        @selected="updateSelection"
        @show="selectionOnShow">
        <!-- toggle -->
        <div slot="toggle" class="sort-toggle">
          <i 
            class="sort-toggle-icon" 
            :class="[
              informationIcon(sort && sort.field && sort.field.type),
              {'sort-toggle-icon-option': sort && sort.field && sort.field.fieldName},
              {'sort-toggle-icon-default': getDefaultSelection(sort && sort.field)}
            ]" />         
          <div class="sort-toggle-name">{{sort && sort.field ? sort.field.label || sort.field.displayName : "Select sort option"}}</div>
        </div>
        <!-- menu -->
        <template #menu="{select, defaultOptionClass}">
          <div class="sort-filter" @click.stop>
            <div class="fts-form-control-filter">
              <input
                class="fts-form-control"
                v-model="sortFilter"
                placeholder="Search something for..."
                ref="selectionFilter"
              />
              <div class="search-icon">
                <font-awesome-icon :icon="['fas', 'search']" fixed-width />
              </div>
            </div>
          </div>
          <div
            class="sort-option"
            :class="defaultOptionClass"
            v-for="col in sortColumns"
            :key="col.id"
            @click.stop="select(col)">
              <i 
                class="sort-option-icon" 
                :class="[
                  informationIcon(col.type),
                  {'default': getDefaultSelection(col)}
                ]"/>
              <div>{{ col.label || col.displayName }}</div>
          </div>
        </template>
      </fts-dropdown-select>
      <!-- direction -->
      <div @click="updateDirection()" class="sort-toggle sort-dir">
        <font-awesome-layers class="sort-dir-layers">
          <font-awesome-icon
            :icon="['fas', 'sort-up']"
            fixed-width
            :class="{ 'sort-dir-disabled': sort.direction === 1 }"
          />
          <font-awesome-icon
            :icon="['fas', 'sort-down']"
            fixed-width
            :class="{ 'sort-dir-disabled': sort.direction === 0 }"
          />
        </font-awesome-layers>
        {{ sort.direction === 0 ? "Ascending" : "Descending" }}
      </div>
      <!-- reset -->
      <fts-btn text icon sm class="sort-action" @click="resetSort" v-if="sort && sort.field">
        <font-awesome-icon :icon="['fas', 'times']" />
      </fts-btn>
    </template>
  </fts-dropdown-select>
</template>

<script>
import commons from "../commons/commons";
import queryLogic from "../commons/queryLogic";

import FTSDropdownSelect from "./FTSDropdownSelect";
import FTSBtn from "./FTSBtn";

export default {
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    sort: {
      type: Object,
      default: () => ({}),
    },
  },
  model: {
    prop: "sort",
    event: "updateSort",
  },
  components: {
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-btn": FTSBtn
  },
  data: () => ({
    referenceClass: null,
    sortFilter: null,
  }),
  methods: {
    informationIcon(type) {
      if (type === "state") 
        type = 7;
      else if (!type && type != 0) 
        type = 27

      return commons.informationIcon(type);
    },
    getDefaultSelection(item) {
      if (item && item.system) return true;

      return !!(queryLogic.querySelectionSuffix.find(x => x.fieldName === item && item.fieldName));
    },
    updateDirection() {
      this.$emit("updateSort", {
        ...this.sort,
        direction: this.sort && this.sort.direction == 0 ? 1 : 0,
      });
    },
    updateSelection(data) {
      this.$emit("updateSort", {
        ...this.sort,
        field: data,
      });
    },
    resetSort() {
      this.$emit("updateSort", {
        direction: 0,
      });
    },
    selectionOnShow() {
      this.sortFilter = null;
      this.$nextTick(() => {
        this.$refs.selectionFilter && this.$refs.selectionFilter.focus();
      });
    },
    onClose() {
      this.$emit("closed");
    },
  },
  computed: {
    sortColumns() {
      let fields = _.cloneDeep(this.fields);
      const createdByColIndex = fields.findIndex(x => x.id === 'createdBy' && x.system) + 1;
      const lastChangeByColIndex = fields.findIndex(x => x.id === 'lastChangeBy' && x.system) + 1;
      /* created date */
      if (
        createdByColIndex &&
        !fields.some(x => x.id === 'createdDate' && x.system)
      ) {
        const createdDateColumn = queryLogic.querySelectionPrefix.find(
          x => x.id === 'createdDate'
        );

        if (createdDateColumn)
          fields.splice(createdByColIndex, 0, { ...createdDateColumn });
      }
      /* last change date */
      if (
        lastChangeByColIndex &&
        !fields.some(x => x.id === 'lastChangeDate' && x.system)
      ) {
        const lastChangeDateColumn = queryLogic.querySelectionPrefix.find(
          x => x.id === 'lastChangeDate'
        );

        if (lastChangeDateColumn)
          fields.splice(lastChangeByColIndex, 0, { ...lastChangeDateColumn });
      }  
      /* exclude */
      fields = fields.filter(x => 
        ![9, 10, 11, 12, 13, 14, 15, 20, 22, 24, 29, 33].some(y => x.type == y) &&
        x.id != 'assignTo'
      );
      /* sort filter */
      if (this.sortFilter)
        return fields.filter(x => 
          x.displayName.toLowerCase().indexOf(this.sortFilter.toLowerCase()) != -1
        );
      /* default */
      return fields;
    }
  },
  mounted() {
    this.referenceClass = `fts-sort-${this._uid}`;
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.sort-toggle {
  @include user-select-none;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--fts-gray-3);
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  .sort-toggle-icon {
    margin-right: .575rem;
    font-size: inherit;
    color: var(--fts-gray-5);
    &.sort-toggle-icon-option {
      color: $secondaryColor;
    }
    &.sort-toggle-icon-default {
      color: $primaryColor;
    }
  }
  .sort-toggle-name {
    @include text-overflow-ellipsis;
    // max-width: 120px;
  }
  &.sort-dir {
    margin-left: 0.75rem;
    .sort-dir-layers {
      margin-right: .575rem;
      svg {
        transition: color .3s ease-in-out;
        font-size: 0.875rem;
        &:not(.sort-dir-disabled) {
          color: var(--fts-gray-5);
        }
        &.sort-dir-disabled {
          color: var(--fts-gray-4);
          opacity: 0.5;
        }
      }
    }
  }
}
.sort-action {
  margin-left: .375rem;
}
.sort-content.sort-dropdown-menu {
  width: 360px;
  max-width: 360px;
  display: flex;
  align-items: center;
  padding: .75rem;
  .sort-info {
    flex: 1;
    min-width: 0;
  }
}
.sort-info-content.sort-info-dropdown-menu {
  padding: 0 0 .375rem;
  .sort-filter {
    @include theme() {
      @include backdrop-background($color: theme-get('fts-popover-background'), $blur: 2px);
    }
    position: sticky;
    top: 0;
    padding: .75rem .575rem calc(0.75rem - 0.375rem);
  }
  .sort-option {
    display: flex;
    align-items: center;
    font-size: .875rem;
    .sort-option-icon {
      margin-right: .575rem;
      font-size: inherit;
      color: $secondaryColor;
      &.default {
        color: $primaryColor;
      }
    }
  }
}
</style>